/**
 * useFilteredMenu Hook
 * Filters menu items based on user permissions
 * 
 * @author JGS Tech Team
 * @version 1.0
 */

import { useMemo, useCallback } from 'react';
import { useAccessControl } from '../contexts/AccessControlContext';
import { getMenuPermissionConfig } from '../config/menuPermissions';
import type { MenuPermissionConfig } from '../types/accessControl';

/**
 * Menu item type (matches menu.ts structure)
 */
interface MenuItem {
    id?: string | number;
    text?: string;
    path?: string;
    icon?: string;
    isDisable?: boolean;
    notification?: string | boolean;
    hide?: boolean;
    subMenu?: Record<string, MenuItem> | null;
}

/**
 * Menu type
 */
type Menu = Record<string, MenuItem>;

/**
 * useFilteredMenu hook
 * Returns a filtered menu based on user permissions
 * 
 * @param menu - The menu object to filter
 * @returns Filtered menu with only accessible items
 * 
 * @example
 * const filteredDashboard = useFilteredMenu(dashboardPagesMenu);
 */
export const useFilteredMenu = (menu: Menu): Menu => {
    const {
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRole,
        hasAnyRole,
        isAuthenticated,
    } = useAccessControl();

    /**
     * Check if user has access to a menu item based on its permission config
     * Logic: User needs EITHER the permission OR the role (OR logic between permission and role checks)
     * Within permission checks: follows AND/OR as specified (anyPermissions = OR, allPermissions = AND)
     * Within role checks: follows AND/OR as specified (anyRoles = OR)
     */
    const hasMenuAccess = useCallback(
        (config?: MenuPermissionConfig): boolean => {
            // No config means accessible to all authenticated users
            if (!config) return true;

            // Always hide from menu if marked
            if (config.hideFromMenu) return false;

            // Hide when authenticated (e.g., login, signup pages)
            if (config.hideWhenAuthenticated && isAuthenticated) return false;
            if (!config) return true;

            // Check if config has any requirements
            const hasPermissionRequirements =
                config.permission ||
                config.anyPermissions?.length ||
                config.allPermissions?.length;
            
            const hasRoleRequirements = 
                config.role ||
                config.anyRoles?.length;

            // No requirements means accessible to all
            if (!hasPermissionRequirements && !hasRoleRequirements) return true;

            // Check permission requirements
            let permissionCheckPassed = false;
            if (hasPermissionRequirements) {
                // For permissions, all specified checks must pass (AND logic within permission checks)
                let singlePermissionOk = true;
                let anyPermissionsOk = true;
                let allPermissionsOk = true;

                if (config.permission) {
                    singlePermissionOk = hasPermission(config.permission);
                }
                if (config.anyPermissions?.length) {
                    anyPermissionsOk = hasAnyPermission(config.anyPermissions);
                }
                if (config.allPermissions?.length) {
                    allPermissionsOk = hasAllPermissions(config.allPermissions);
                }

                permissionCheckPassed = singlePermissionOk && anyPermissionsOk && allPermissionsOk;
            }

            // Check role requirements
            let roleCheckPassed = false;
            if (hasRoleRequirements) {
                let singleRoleOk = true;
                let anyRolesOk = true;

                if (config.role) {
                    singleRoleOk = hasRole(config.role);
                }
                if (config.anyRoles?.length) {
                    anyRolesOk = hasAnyRole(config.anyRoles);
                }

                roleCheckPassed = singleRoleOk && anyRolesOk;
            }

            // User needs EITHER permission OR role to have access
            // If only permissions are specified, check permissions
            // If only roles are specified, check roles
            // If both are specified, user needs EITHER to pass
            if (hasPermissionRequirements && hasRoleRequirements) {
                return permissionCheckPassed || roleCheckPassed;
            } else if (hasPermissionRequirements) {
                return permissionCheckPassed;
            } else if (hasRoleRequirements) {
                return roleCheckPassed;
            }

            return true;
        },
        [hasPermission, hasAllPermissions, hasAnyPermission, hasRole, hasAnyRole]
    );

    /**
     * Filter a single menu item and its submenus
     */
    const filterMenuItem = useCallback(
        (item: MenuItem, itemId: string): MenuItem | null => {
            // Get permission config for this menu item
            const config = getMenuPermissionConfig(itemId);

            // Check if user has access
            if (!hasMenuAccess(config)) {
                return null;
            }

            // If item has subMenu, filter it recursively
            if (item.subMenu) {
                const filteredSubMenu: Record<string, MenuItem> = {};
                let hasVisibleSubItems = false;

                Object.entries(item.subMenu).forEach(([subId, subItem]) => {
                    const filtered = filterMenuItem(subItem, subId);
                    if (filtered) {
                        filteredSubMenu[subId] = filtered;
                        hasVisibleSubItems = true;
                    }
                });

                // If all sub-items are filtered out, hide the parent too
                if (!hasVisibleSubItems && Object.keys(filteredSubMenu).length === 0) {
                    return null;
                }

                return {
                    ...item,
                    subMenu: Object.keys(filteredSubMenu).length > 0 ? filteredSubMenu : null,
                };
            }

            return item;
        },
        [hasMenuAccess]
    );

    // Filter the entire menu
    const filteredMenu = useMemo(() => {
        const result: Menu = {};

        Object.entries(menu).forEach(([id, item]) => {
            const filtered = filterMenuItem(item, id);
            if (filtered) {
                result[id] = filtered;
            }
        });

        return result;
    }, [menu, filterMenuItem]);

    return filteredMenu;
};

/**
 * useMenuItemAccess hook
 * Check if user has access to a specific menu item
 * 
 * @param menuId - The menu item id
 * @returns boolean indicating if user has access
 * 
 * @example
 * const canAccessAnalytics = useMenuItemAccess('analytics');
 */
export const useMenuItemAccess = (menuId: string): boolean => {
    const {
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRole,
        hasAnyRole,
    } = useAccessControl();

    return useMemo(() => {
        const config = getMenuPermissionConfig(menuId);

        // No config means accessible to all authenticated users
        if (!config) return true;

        // Check if config has any requirements
        const hasPermissionRequirements =
            config.permission ||
            config.anyPermissions?.length ||
            config.allPermissions?.length;
        
        const hasRoleRequirements = 
            config.role ||
            config.anyRoles?.length;

        // No requirements means accessible to all
        if (!hasPermissionRequirements && !hasRoleRequirements) return true;

        // Check permission requirements
        let permissionCheckPassed = false;
        if (hasPermissionRequirements) {
            let singlePermissionOk = true;
            let anyPermissionsOk = true;
            let allPermissionsOk = true;

            if (config.permission) {
                singlePermissionOk = hasPermission(config.permission);
            }
            if (config.anyPermissions?.length) {
                anyPermissionsOk = hasAnyPermission(config.anyPermissions);
            }
            if (config.allPermissions?.length) {
                allPermissionsOk = hasAllPermissions(config.allPermissions);
            }

            permissionCheckPassed = singlePermissionOk && anyPermissionsOk && allPermissionsOk;
        }

        // Check role requirements
        let roleCheckPassed = false;
        if (hasRoleRequirements) {
            let singleRoleOk = true;
            let anyRolesOk = true;

            if (config.role) {
                singleRoleOk = hasRole(config.role);
            }
            if (config.anyRoles?.length) {
                anyRolesOk = hasAnyRole(config.anyRoles);
            }

            roleCheckPassed = singleRoleOk && anyRolesOk;
        }

        // User needs EITHER permission OR role to have access
        if (hasPermissionRequirements && hasRoleRequirements) {
            return permissionCheckPassed || roleCheckPassed;
        } else if (hasPermissionRequirements) {
            return permissionCheckPassed;
        } else if (hasRoleRequirements) {
            return roleCheckPassed;
        }

        return true;
    }, [
        menuId,
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRole,
        hasAnyRole,
    ]);
};

export default useFilteredMenu;
